import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Clock, Star, ArrowLeft } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const RECENT_KEY = "suhool_recent_searches";
const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } };

export default function SearchOverlay({ open, onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState(getRecent);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/categories").then((r) => setCategories(r.data.filter((c) => c.count > 0))).catch(() => {});
      setTimeout(() => inputRef.current?.focus(), 80);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setResults(null); setLoading(false); setError(false); return; }
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      api.get("/products", { params: { q: term, limit: 8 }, signal: ctrl.signal })
        .then((r) => { setResults(r.data); setLoading(false); })
        .catch((err) => { if (err.code !== "ERR_CANCELED") { setError(true); setLoading(false); } });
    }, 300);
    return () => clearTimeout(timer);
  }, [q, open]);

  const saveRecent = (term) => {
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, 6);
    setRecent(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  const go = (path, term) => {
    if (term) saveRecent(term);
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] bg-olive-deep/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-20 max-h-[75vh] w-[calc(100vw-2rem)] max-w-2xl overflow-hidden rounded-3xl bg-ivory shadow-2xl sm:mt-28"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-olive/10 px-5 py-4">
              <Search className="h-5 w-5 shrink-0 text-olive" />
              <input
                ref={inputRef}
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) go(`/store?q=${encodeURIComponent(q.trim())}`, q.trim()); }}
                placeholder={t("search_hint")}
                className="w-full bg-transparent text-base outline-none placeholder:text-ink/40"
              />
              <button data-testid="search-close" onClick={onClose} aria-label="إغلاق البحث"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-olive/5 text-ink/60 transition-colors hover:bg-olive/10">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[calc(75vh-4.5rem)] overflow-y-auto p-5">
              {loading && (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="h-14 w-14 animate-pulse rounded-xl bg-olive/8" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-1/2 animate-pulse rounded-full bg-olive/10" />
                        <div className="h-3 w-1/4 animate-pulse rounded-full bg-olive/8" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && error && (
                <div className="py-10 text-center">
                  <p className="text-ink/60">{t("error_generic")}</p>
                  <button data-testid="search-retry" onClick={() => setQ((v) => v + " ")}
                    className="mt-3 rounded-full bg-olive px-5 py-2 text-sm font-semibold text-ivory">{t("retry")}</button>
                </div>
              )}

              {!loading && !error && results && (
                results.length === 0 ? (
                  <div data-testid="search-empty" className="py-10 text-center text-ink/60">{t("search_no_results")}</div>
                ) : (
                  <div className="space-y-1.5">
                    {results.map((p) => (
                      <button key={p.sku} data-testid={`search-result-${p.sku}`}
                        onClick={() => go(`/product/${p.slug}`, q.trim())}
                        className="flex w-full items-center gap-3 rounded-2xl p-2 text-right transition-colors hover:bg-olive/5">
                        <img src={p.images?.main} alt={p.name_ar} loading="lazy" decoding="async"
                          className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-display text-sm font-semibold text-ink">{p.name_ar}</span>
                          <span className="block text-xs text-ink/50">{p.region} · {fmtPrice(p.price)}</span>
                        </span>
                        {p.rating > 0 && (
                          <span className="flex items-center gap-1 text-xs text-ink/50">
                            <Star className="h-3.5 w-3.5 fill-gold text-gold" />{p.rating}
                          </span>
                        )}
                      </button>
                    ))}
                    <button data-testid="search-view-all"
                      onClick={() => go(`/store?q=${encodeURIComponent(q.trim())}`, q.trim())}
                      className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-2xl border border-olive/15 py-3 text-sm font-medium text-olive transition-colors hover:bg-olive/5">
                      {t("search_view_all")} <ArrowLeft className="h-4 w-4" />
                    </button>
                  </div>
                )
              )}

              {!loading && !error && !results && (
                <div className="space-y-6">
                  {recent.length > 0 && (
                    <div>
                      <div className="mb-2 text-xs font-semibold text-ink/50">{t("search_recent")}</div>
                      <div className="flex flex-wrap gap-2">
                        {recent.map((r) => (
                          <button key={r} data-testid="search-recent-chip" onClick={() => setQ(r)}
                            className="flex items-center gap-1.5 rounded-full border border-olive/15 bg-white px-3.5 py-1.5 text-sm text-ink/70 transition-colors hover:border-olive/40">
                            <Clock className="h-3.5 w-3.5 text-olive/60" /> {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="mb-2 text-xs font-semibold text-ink/50">{t("search_categories")}</div>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((c) => (
                        <button key={c.key} data-testid={`search-category-${c.key}`}
                          onClick={() => go(`/store?category=${c.key}`)}
                          className="rounded-full bg-olive/8 px-4 py-1.5 text-sm font-medium text-olive-dark transition-colors hover:bg-olive hover:text-ivory">
                          {c.name_ar}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
